import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow 
} from '../components/ui/table';
import { 
    Users, 
    TrendingUp, 
    CheckCircle2,
    XCircle,
    Clock,
    Filter,
    Download,
    Loader2,
    CheckSquare,
    Archive,
    RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { InviteLinkCard } from '../components/InviteLinkCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
    const styles = {
        new: 'bg-blue-50 text-blue-700 border-blue-200',
        contacted: 'bg-amber-50 text-amber-700 border-amber-200',
        qualified: 'bg-purple-50 text-purple-700 border-purple-200',
        converted: 'bg-green-50 text-green-700 border-green-200',
        rejected: 'bg-red-50 text-red-700 border-red-200'
    };
    
    return (
        <Badge variant="outline" className={styles[status] || styles.new}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
    );
};

export const AdminReferralsPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [referrals, setReferrals] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [updating, setUpdating] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showArchived, setShowArchived] = useState(false);
    const [archiving, setArchiving] = useState(null);

    useEffect(() => {
        fetchData();
    }, [showArchived]);

    // Reset selection when filter changes
    useEffect(() => {
        setSelectedIds(new Set());
    }, [statusFilter, showArchived]);

    const fetchData = async () => {
        try {
            const [referralsRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/referrals`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { include_archived: showArchived }
                }),
                axios.get(`${API}/admin/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setReferrals(referralsRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Daten konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (referralId, newStatus) => {
        setUpdating(referralId);
        try {
            await axios.put(
                `${API}/admin/referrals/${referralId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Status aktualisiert`);
            fetchData(); // Refresh data
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Status konnte nicht aktualisiert werden');
        } finally {
            setUpdating(null);
        }
    };

    const archiveReferral = async (referralId) => {
        setArchiving(referralId);
        try {
            await axios.put(
                `${API}/admin/referrals/${referralId}/archive`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Empfehlung archiviert');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Archivieren fehlgeschlagen');
        } finally {
            setArchiving(null);
        }
    };

    const restoreReferral = async (referralId) => {
        setArchiving(referralId);
        try {
            await axios.put(
                `${API}/admin/referrals/${referralId}/restore`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Empfehlung wiederhergestellt');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Wiederherstellen fehlgeschlagen');
        } finally {
            setArchiving(null);
        }
    };

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            // Build URL with selected IDs if any
            let url = `${API}/admin/referrals/export`;
            if (selectedIds.size > 0) {
                const idsParam = Array.from(selectedIds).join(',');
                url += `?ids=${encodeURIComponent(idsParam)}`;
            }
            
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            
            // Create download link
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-16le;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Get filename from response or generate
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'empfehlungen_export.csv';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename=(.+)/);
                if (match) filename = match[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            const count = selectedIds.size > 0 ? selectedIds.size : filteredReferrals.length;
            toast.success(`${count} Empfehlung${count !== 1 ? 'en' : ''} exportiert`);
            
            // Clear selection after export
            setSelectedIds(new Set());
        } catch (error) {
            toast.error('Export fehlgeschlagen');
        } finally {
            setExporting(false);
        }
    };

    // Selection handlers
    const toggleSelectAll = () => {
        if (selectedIds.size === filteredReferrals.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredReferrals.map(r => r.id)));
        }
    };

    const toggleSelect = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const filteredReferrals = statusFilter === 'all' 
        ? referrals 
        : referrals.filter(r => r.status === statusFilter);

    const allSelected = filteredReferrals.length > 0 && selectedIds.size === filteredReferrals.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < filteredReferrals.length;

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Invite Link Card */}
                <InviteLinkCard />
                
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Empfehlungen</h1>
                        <p className="text-slate-600 mt-1">
                            Verwalten Sie alle eingereichten Empfehlungen
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {selectedIds.size > 0 && (
                            <span className="text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                                {selectedIds.size} ausgewählt
                            </span>
                        )}
                        <Button
                            onClick={handleExportCSV}
                            disabled={exporting || referrals.length === 0}
                            variant="outline"
                            className="gap-2"
                            data-testid="export-csv-btn"
                        >
                            {exporting ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Exportiere...
                                </>
                            ) : (
                                <>
                                    <Download size={16} />
                                    {selectedIds.size > 0 
                                        ? `${selectedIds.size} exportieren` 
                                        : 'Alle exportieren'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card data-testid="total-referrals-stat">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                                    <Users size={24} className="text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Gesamt Empfehlungen</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {stats?.total_referrals || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="converted-stat">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                                    <CheckCircle2 size={24} className="text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Konvertiert</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {stats?.converted_referrals || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="conversion-rate-stat">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                                    <TrendingUp size={24} className="text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Konversionsrate</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {stats?.conversion_rate || 0}%
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="total-users-stat">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div 
                                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                >
                                    <Users size={24} style={{ color: tenant?.primary_color }} />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Aktive Nutzer</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {stats?.total_users || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Referrals Table */}
                <Card data-testid="referrals-table-card">
                    <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <CardTitle>Alle Empfehlungen</CardTitle>
                        <div className="flex items-center gap-4">
                            {/* Archive Toggle */}
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox
                                    checked={showArchived}
                                    onCheckedChange={setShowArchived}
                                    data-testid="show-archived-checkbox"
                                />
                                <span className="text-slate-600">
                                    <Archive size={14} className="inline mr-1" />
                                    Archivierte anzeigen
                                </span>
                            </label>
                            
                            {/* Status Filter */}
                            <div className="flex items-center gap-2">
                                <Filter size={18} className="text-slate-400" />
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-40" data-testid="status-filter">
                                        <SelectValue placeholder="Status filtern" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Alle Status</SelectItem>
                                        <SelectItem value="new">Neu</SelectItem>
                                        <SelectItem value="contacted">Kontaktiert</SelectItem>
                                        <SelectItem value="qualified">Qualifiziert</SelectItem>
                                        <SelectItem value="converted">Konvertiert</SelectItem>
                                        <SelectItem value="rejected">Abgelehnt</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-3">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                        ) : filteredReferrals.length === 0 ? (
                            <div className="text-center py-12">
                                <Users size={48} className="mx-auto text-slate-300 mb-3" />
                                <p className="text-slate-500">Keine Empfehlungen gefunden</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12">
                                                <Checkbox
                                                    checked={allSelected}
                                                    onCheckedChange={toggleSelectAll}
                                                    aria-label="Alle auswählen"
                                                    data-testid="select-all-checkbox"
                                                    className={someSelected ? "data-[state=checked]:bg-slate-400" : ""}
                                                />
                                            </TableHead>
                                            <TableHead>Empfehlung</TableHead>
                                            <TableHead>Empfohlen von</TableHead>
                                            <TableHead>Vertriebler</TableHead>
                                            <TableHead>Datum</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Aktionen</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredReferrals.map((referral) => (
                                            <TableRow 
                                                key={referral.id} 
                                                data-testid={`referral-row-${referral.id}`}
                                                className={`${selectedIds.has(referral.id) ? "bg-blue-50" : ""} ${referral.archived ? "opacity-60" : ""}`}
                                            >
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedIds.has(referral.id)}
                                                        onCheckedChange={() => toggleSelect(referral.id)}
                                                        aria-label={`${referral.lead_name} auswählen`}
                                                        data-testid={`select-${referral.id}`}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium text-slate-900">
                                                            {referral.lead_name}
                                                            {referral.archived && (
                                                                <Badge variant="outline" className="ml-2 text-xs bg-slate-100 text-slate-500">
                                                                    Archiviert
                                                                </Badge>
                                                            )}
                                                        </p>
                                                        <p className="text-sm text-slate-500">{referral.lead_email}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-slate-600">{referral.user_name}</span>
                                                </TableCell>
                                                <TableCell>
                                                    {referral.sales_rep_name ? (
                                                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                            {referral.sales_rep_name}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-slate-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-slate-600">{formatDate(referral.created_at)}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge status={referral.status} />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Select 
                                                            value={referral.status}
                                                            onValueChange={(value) => updateStatus(referral.id, value)}
                                                            disabled={updating === referral.id || referral.archived}
                                                        >
                                                            <SelectTrigger 
                                                                className="w-32"
                                                                data-testid={`status-select-${referral.id}`}
                                                            >
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="new">Neu</SelectItem>
                                                                <SelectItem value="contacted">Kontaktiert</SelectItem>
                                                                <SelectItem value="qualified">Qualifiziert</SelectItem>
                                                                <SelectItem value="converted">Konvertiert</SelectItem>
                                                                <SelectItem value="rejected">Abgelehnt</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                        
                                                        {referral.archived ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => restoreReferral(referral.id)}
                                                                disabled={archiving === referral.id}
                                                                title="Wiederherstellen"
                                                                data-testid={`restore-${referral.id}`}
                                                            >
                                                                {archiving === referral.id ? (
                                                                    <Loader2 size={16} className="animate-spin" />
                                                                ) : (
                                                                    <RotateCcw size={16} className="text-green-600" />
                                                                )}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => archiveReferral(referral.id)}
                                                                disabled={archiving === referral.id}
                                                                title="Archivieren"
                                                                data-testid={`archive-${referral.id}`}
                                                            >
                                                                {archiving === referral.id ? (
                                                                    <Loader2 size={16} className="animate-spin" />
                                                                ) : (
                                                                    <Archive size={16} className="text-slate-400 hover:text-slate-600" />
                                                                )}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
};
